import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast, ToastType } from 'services/toast';
import { Paths } from 'services/navigation/paths';
import { useCampaignCreationStore } from 'modules/campaign-creation/stores/useCampaignCreationStore';
import { LaunchOption } from 'modules/campaign-creation/components/LaunchCampaignConfirmModal';
import { AutomationTaskInput } from 'services/automation/types';

export type ActiveModal =
  | 'none'
  | 'confirm'
  | 'countdown'
  | 'schedule'
  | 'initiateLaunch';

/**
 * Hook that manages the campaign launch flow including:
 * - Modal states (only one modal can be open at a time)
 * - Saving automation tasks + launch config and advancing to CREATE step
 * - Draft flow (toast + navigate)
 */
export const useCampaignLaunchFlow = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const automationTasksRef = useRef<AutomationTaskInput[]>([]);

  const [activeModal, setActiveModal] = useState<ActiveModal>('none');

  const onAutomationTasksSubmit = useCampaignCreationStore(
    (s) => s.onAutomationTasksSubmit
  );
  const onAutomationTasksSubmitWithLaunchConfig = useCampaignCreationStore(
    (s) => s.onAutomationTasksSubmitWithLaunchConfig
  );

  const closeModal = () => setActiveModal('none');

  const handleSaveAsDraft = () => {
    closeModal();
    showToast({ message: 'Draft campaign saved', type: ToastType.Success });
    navigate(Paths.CAMPAIGNS);
  };

  const handleConfirmOption = (option: LaunchOption) => {
    if (option === LaunchOption.Draft) {
      handleSaveAsDraft();
    } else if (option === LaunchOption.Now) {
      setActiveModal('countdown');
    }
  };

  const handleCountdownComplete = () => {
    closeModal();
    onAutomationTasksSubmitWithLaunchConfig({
      automationTasks: automationTasksRef.current,
      launchConfig: { launchNow: true }
    });
  };

  const handleScheduleConfirm = (date: Date) => {
    closeModal();
    onAutomationTasksSubmitWithLaunchConfig({
      automationTasks: automationTasksRef.current,
      launchConfig: { launchDate: date }
    });
  };

  // For CSV campaigns: just submit (no launch config, messages need to be generated first)
  const handleInitiateLaunchConfirmCsv = () => {
    closeModal();
    onAutomationTasksSubmit({ automationTasks: automationTasksRef.current });
  };

  // For non-CSV campaigns: show countdown modal before launching
  const handleInitiateLaunchConfirmNonCsv = () => {
    setActiveModal('countdown');
  };

  const handleCountdownAbort = () => {
    closeModal();
    showToast({ message: 'Campaign launch aborted', type: ToastType.Success });
  };

  return {
    activeModal,
    openConfirm: () => setActiveModal('confirm'),
    openInitiateLaunch: () => setActiveModal('initiateLaunch'),
    openSchedule: () => setActiveModal('schedule'),
    closeModal,
    setAutomationTasks: (tasks: AutomationTaskInput[]) => {
      automationTasksRef.current = tasks;
    },
    onConfirmOption: handleConfirmOption,
    onSaveAsDraft: handleSaveAsDraft,
    onCountdownComplete: handleCountdownComplete,
    onCountdownAbort: handleCountdownAbort,
    onScheduleConfirm: handleScheduleConfirm,
    onInitiateLaunchConfirmCsv: handleInitiateLaunchConfirmCsv,
    onInitiateLaunchConfirmNonCsv: handleInitiateLaunchConfirmNonCsv
  };
};
